import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, Sparkles, ArrowUpRight, Download } from "lucide-react";
import { Link } from "react-router-dom";

export default function ScanReport() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file && !text.trim()) {
      toast.error("Upload a PDF or paste your report text");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      if (text.trim()) fd.append("text", text);
      const { data } = await api.post("/scan/report", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      const bureauMsg = data.detected_bureau
        ? data.detected_bureau === "tri_merge"
          ? " · Tri-merge detected"
          : ` · ${data.detected_bureau.charAt(0).toUpperCase() + data.detected_bureau.slice(1)} report detected`
        : "";
      toast.success(`Found ${data.items.length} disputable items${bureauMsg}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell>
      <div className="mb-10">
        <div className="overline mb-3">AI Scan</div>
        <h1 className="font-display text-5xl md:text-6xl leading-none">Scan your report.</h1>
        <p className="text-[#A8ADA5] mt-3 max-w-2xl">
          Upload a PDF from AnnualCreditReport.com, Credit Karma, or Experian — or paste the text.
          Claude Sonnet 4.5 reads every line and flags disputable items in seconds.
        </p>
      </div>

      <Link
        to="/app/get-report"
        data-testid="scan-getreport-cta"
        className="card-surface p-5 mb-8 flex items-center justify-between gap-3 group hover:border-[#D5FF3F]/40 transition-colors"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-sm bg-[#D5FF3F]/10 border border-[#D5FF3F]/20 flex items-center justify-center flex-shrink-0">
            <Download size={16} className="text-[#D5FF3F]" />
          </div>
          <div>
            <div className="font-medium">Don&rsquo;t have your report yet?</div>
            <div className="text-xs text-[#A8ADA5] mt-0.5">
              5 in-app guided walkthroughs · bureau sites open in a floating window so Aura stays with you
            </div>
          </div>
        </div>
        <ArrowUpRight
          size={16}
          className="text-[#6D716C] group-hover:text-[#D5FF3F] flex-shrink-0"
        />
      </Link>

      <form onSubmit={submit} className="space-y-6" data-testid="scan-form">
        <div className="card-surface p-8">
          <div className="overline mb-4">A · Upload a file</div>
          <label
            htmlFor="file"
            className="block border border-dashed border-[#3A3D42] rounded-sm p-12 text-center cursor-pointer hover:border-[#D5FF3F]/60 hover:bg-[#D5FF3F]/[0.02] transition-colors"
            data-testid="scan-upload-dropzone"
          >
            <Upload size={26} strokeWidth={1.5} className="mx-auto text-[#D5FF3F] mb-4" />
            <div className="font-medium text-[#F5F3EE]">
              {file ? file.name : "Click to select a PDF or .txt file"}
            </div>
            <div className="text-xs text-[#6D716C] mt-2 font-mono uppercase tracking-widest">Max 40k chars parsed</div>
            <input
              id="file"
              type="file"
              accept=".pdf,.txt"
              className="hidden"
              data-testid="scan-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </label>
        </div>

        <div className="card-surface p-8">
          <div className="overline mb-4">B · Paste report text</div>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste your credit report text here (or excerpts of the trade lines you want reviewed)…"
            rows={10}
            data-testid="scan-text-input"
            className="rounded-sm font-mono text-sm"
          />
        </div>

        <div className="flex items-center justify-between gap-4 flex-wrap">
          <p className="text-xs text-[#6D716C] max-w-md leading-relaxed">
            Your report content is used only for this scan. Not shared with third parties. Not used to train models.
          </p>
          <Button
            type="submit"
            disabled={loading}
            data-testid="scan-submit-btn"
            className="rounded-full btn-lime h-12 px-6"
          >
            {loading ? (
              <>Scanning…</>
            ) : (
              <>
                <Sparkles size={16} className="mr-2" /> Run AI Scan
              </>
            )}
          </Button>
        </div>
      </form>

      {result && (
        <div className="mt-10 fade-in" data-testid="scan-result">
          <div className="card-surface p-8 mb-6 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#D5FF3F]/10 blur-3xl" />
            <div className="relative">
              <div className="overline mb-2">Scan complete</div>
              <div className="font-display text-4xl">
                <span className="text-[#D5FF3F]">{result.items.length}</span> disputable items found.
              </div>
              {result.detected_bureau && (
                <div
                  className="mt-4 inline-flex items-center gap-2 border border-[#D5FF3F]/30 bg-[#D5FF3F]/5 rounded-full px-3 py-1.5"
                  data-testid="scan-detected-bureau"
                >
                  <Sparkles size={11} className="text-[#D5FF3F]" />
                  <span className="text-xs font-mono uppercase tracking-widest text-[#D5FF3F]">
                    Auto-detected ·{" "}
                    {result.detected_bureau === "tri_merge"
                      ? "Tri-Merge (all 3 bureaus)"
                      : result.detected_bureau.charAt(0).toUpperCase() +
                        result.detected_bureau.slice(1)}
                    {result.detection_confidence && result.detection_confidence !== "high" && (
                      <span className="text-[#A8ADA5] ml-1.5">
                        ({result.detection_confidence} confidence)
                      </span>
                    )}
                  </span>
                </div>
              )}
              {result.summary && (
                <p className="text-[#A8ADA5] mt-4 max-w-2xl leading-relaxed">{result.summary}</p>
              )}
              {result.estimated_score && (
                <div className="mt-5 text-sm text-[#6D716C] font-mono">
                  Estimated score: <span className="text-[#F5F3EE] text-base">{result.estimated_score}</span>
                </div>
              )}
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {result.items.map((item) => (
              <div key={item.id} className="card-surface p-5 lift">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium text-[#F5F3EE]">{item.creditor}</div>
                    <div className="text-xs text-[#6D716C] mt-1 font-mono uppercase tracking-wider">
                      {item.item_type?.replace("_", " ")}
                      {item.amount ? ` · $${item.amount}` : ""}
                      {item.bureau && item.bureau !== "all" && (
                        <span className="text-[#D5FF3F] ml-2">· {item.bureau}</span>
                      )}
                    </div>
                  </div>
                  <span
                    className={`text-[10px] px-2 py-1 rounded-full font-mono uppercase tracking-wider ${
                      item.severity === "high"
                        ? "bg-[#FF6B4A]/15 text-[#FF6B4A]"
                        : item.severity === "medium"
                        ? "bg-[#D5FF3F]/15 text-[#D5FF3F]"
                        : "bg-[#7CFF9B]/15 text-[#7CFF9B]"
                    }`}
                  >
                    {item.severity || "flagged"}
                  </span>
                </div>
                {item.reason && (
                  <p className="text-sm text-[#A8ADA5] mt-3 leading-relaxed">{item.reason}</p>
                )}
              </div>
            ))}
          </div>
          <div className="mt-8 flex gap-3">
            <Button
              onClick={() => navigate("/app/items")}
              data-testid="scan-go-items"
              className="rounded-full btn-lime"
            >
              Review items <ArrowUpRight size={15} className="ml-1.5" />
            </Button>
            <Button
              onClick={() => navigate("/app/disputes")}
              data-testid="scan-go-disputes"
              className="rounded-full btn-outline"
            >
              Go to disputes
            </Button>
          </div>
        </div>
      )}
    </AppShell>
  );
}
